/**
 * CNC prírezy — zrkadlo backend pravidla (pricing/calculator.py
 * `cnc_prirezy`) LEN na zobrazenie v UI. Autoritatívne čísla počíta
 * backend (cena, porez); táto funkcia ukazuje používateľovi, aký prírez
 * dielec reálne dostane a AKÁ OPERÁCIA sa s ním robí.
 *
 * Dielec má dve čísla, ktoré si nesmú protirečiť:
 *   • kusy    = čo dostane zákazník (kusovník, cena) — nikdy sa nemenia,
 *   • prírezy = čo reálne ide na stroj — vždy odvodené z kusov.
 *
 * Pravidlo (port VBA „nadmiery"): CNC dielec s čistým rozmerom pod
 * minimom stroja dostane väčší prírez — hrubý = max(čistá, min) +
 * nadmiera; úzke kusy sa združujú do jedného prírezu — toľko kusov,
 * koľko ich treba na dosiahnutie minima stroja.
 */

/** Operácia prírezu — mirror backend OPERACIA_* konštánt. */
export type CncOperacia =
  | "" // dielec vyhovuje stroju
  | "rez" // predrez na minimum upnutia
  | "zdruz_rez" // viac kusov v jednom príreze
  | "rot_rez" // otočený o 90° kvôli čelám
  | "rot_zdruz_rez"; // rotácia + združenie

/** Slovenské popisy operácií (mirror backend CNC_OPERACIE_POPIS). */
export const CNC_OPERACIE_POPIS: Record<Exclude<CncOperacia, "">, string> = {
  rez: "Predrez na minimum stroja",
  zdruz_rez: "Združenie kusov do jedného prírezu",
  rot_rez: "Rotácia o 90° kvôli čelám",
  rot_zdruz_rez: "Rotácia + združenie",
};

/** Defaultné názvy operácií (mirror DEFAULT_CNC_NAZOV_*). */
export const CNC_NAZVY_DEFAULT: Record<Exclude<CncOperacia, "">, string> = {
  rez: "nc_rez",
  zdruz_rez: "nc_zdruz_rez",
  rot_rez: "nc_rot_rez",
  rot_zdruz_rez: "nc_rot_zdruz_rez",
};

export interface CncUpnutie {
  minDlzkaMm: number;
  minSirkaMm: number;
  zdruzenieMedzeraMm: number;
  /** Rotačný priestor stroja (mm); 0 = rotácia vypnutá. */
  rotaciaMaxMm: number;
  /** Názvy operácií z Nastavení (prázdne → default). */
  nazvy?: Partial<Record<Exclude<CncOperacia, "">, string>>;
}

/** Part typy auto-rotované kvôli čelám (mirror backend AUTO_ROTATE_NAZVY). */
const AUTO_ROTATE_NAZVY = new Set(["zas_chrb", "zas_celo"]);

/**
 * Ide dielec na CNC stroj? Mirror backend `pricing.calculator.ide_na_nc`.
 *
 * Rozhoduje **príznak výrobného postupu** (`Operation.je_cnc`) a override
 * na dielci (`Dielec.ide_na_cnc`) ho prebíja — tri-state „Auto / Áno /
 * Nie" v detaile je rozhodnutie usera.
 *
 * Do KOLA 108 sa to tu aj na backende hádalo z **tvaru mena** (mirror VBA
 * `ContainsNC`): `"olep_nc" in kod` → nie, `"nc" in kod` → áno. Len čo si
 * user pomenoval postup `lep_nc_olep_nc`, pravidlo padlo — ten reťazec
 * podreťazec `olep_nc` obsahuje, takže by dielec z CNC vypadol a tabuľka
 * by ukazovala iný prírez, než sa reže. Postupy si user pomenúva sám,
 * takže meno rozhodovať nesmie.
 *
 * Do KOLA 105 tu bol fallback na `nadmiera_aktivna`, ktorá tým niesla dve
 * veci naraz — zapnúť odpočet hrán olepovanému dielcu by mu zároveň zaplo
 * minimum upnutia a združovanie.
 */
export function ideNaNc(
  operaciaJeCnc: boolean | null | undefined,
  ideNaCnc: boolean | null | undefined,
): boolean {
  if (ideNaCnc != null) return ideNaCnc;
  return Boolean(operaciaJeCnc);
}

/**
 * Má sa dielec otáčať kvôli operáciám na čelách? `override` null → auto
 * podľa názvu (zas_chrb/zas_celo); true/false → manuálny toggle.
 */
export function dielecRotovat(
  nazovStrojovy: string | null | undefined,
  override: boolean | null | undefined,
): boolean {
  if (override != null) return override;
  return AUTO_ROTATE_NAZVY.has((nazovStrojovy ?? "").trim().toLowerCase());
}

export interface CncPrirez {
  /** Hrubý rozmer prírezu v osi X dielca (mm). */
  x: number;
  /** Hrubý rozmer prírezu v osi Y dielca (mm). */
  y: number;
  /** Koľko takýchto prírezov. */
  pocet: number;
  /** Kusov dielca v jednom príreze (>1 = združený). */
  kusovVPrireze: number;
  operacia: CncOperacia;
}

/**
 * Orientačná normalizácia osí — dlhšia čistá strana ide proti min. dĺžke
 * stroja, kratšia proti min. šírke (dielec 80×300 minimu 200×50 vyhovuje
 * otočený). Zdieľajú ju `cncPrirezy` aj `rozlozKusy`, aby pravidlo
 * nežilo dvakrát.
 */
function osiDielca(
  xMm: number,
  yMm: number,
  nadmieraXMm = 0,
  nadmieraYMm = 0,
): {
  xJeDlha: boolean;
  dlha: number;
  kratka: number;
  nadDlha: number;
  nadKratka: number;
} {
  const xJeDlha = xMm >= yMm;
  const [dlha, kratka] = xJeDlha ? [xMm, yMm] : [yMm, xMm];
  const [nadDlha, nadKratka] = xJeDlha
    ? [nadmieraXMm, nadmieraYMm]
    : [nadmieraYMm, nadmieraXMm];
  return { xJeDlha, dlha, kratka, nadDlha, nadKratka };
}

/** Šírka pásu s `n` kusmi vedľa seba (rezy píly medzi nimi). */
function sirkaPasu(n: number, kratka: number, medzera: number): number {
  return n * kratka + (n - 1) * medzera;
}

export function cncPrirezy(
  xMm: number,
  yMm: number,
  nadmieraXMm: number,
  nadmieraYMm: number,
  pocet: number,
  cnc: CncUpnutie | null,
  rotovat = false,
): CncPrirez[] {
  if (pocet < 1) return [];
  const aktivne = cnc != null && (cnc.minDlzkaMm > 0 || cnc.minSirkaMm > 0);
  if (!aktivne) {
    return [
      {
        x: xMm + nadmieraXMm,
        y: yMm + nadmieraYMm,
        pocet,
        kusovVPrireze: 1,
        operacia: "",
      },
    ];
  }
  const { xJeDlha, dlha, kratka, nadDlha, nadKratka } = osiDielca(
    xMm,
    yMm,
    nadmieraXMm,
    nadmieraYMm,
  );

  const prirez = (
    hd: number,
    hk: number,
    n: number,
    kusov: number,
    operacia: CncOperacia,
  ): CncPrirez =>
    xJeDlha
      ? { x: hd, y: hk, pocet: n, kusovVPrireze: kusov, operacia }
      : { x: hk, y: hd, pocet: n, kusovVPrireze: kusov, operacia };

  const sirkaZdruzenia = (n: number) =>
    sirkaPasu(n, kratka, cnc.zdruzenieMedzeraMm);

  /**
   * Nepusť prírez pod minimum stroja.
   *
   * Nadmiera je od KOLA 104 znamienková (olepený dielec sa reže menší
   * o predfrez) a pripočítava sa až po clampe, takže by vedela stlačiť
   * práve doclampnutý kus naspäť pod minimum. Zrkadlí
   * `pricing/calculator.py::_nad_minimum`.
   */
  const nadMinimum = (hodnota: number, minimum: number) =>
    minimum > 0 ? Math.max(hodnota, minimum) : hodnota;

  /** Rozdelí kusy na plné prírezy po `vPrireze` + zvyšok. */
  const rozdel = (
    vPrireze: number,
    stavitel: (n: number) => CncPrirez,
  ): CncPrirez[] => {
    if (vPrireze <= 1) return [{ ...stavitel(1), pocet }];
    const plnych = Math.floor(pocet / vPrireze);
    const zvysok = pocet % vPrireze;
    const out: CncPrirez[] = [];
    if (plnych > 0) out.push({ ...stavitel(vPrireze), pocet: plnych });
    if (zvysok > 0) out.push(stavitel(zvysok));
    return out;
  };

  // Rotácia (kvôli čelám) — len ak sa zmestí do rotačného priestoru
  if (rotovat && cnc.rotaciaMaxMm > 0 && dlha <= cnc.rotaciaMaxMm) {
    const dimDlhaOs = nadMinimum(cnc.minDlzkaMm + nadDlha, cnc.minDlzkaMm);
    const dimKratkaOs = nadMinimum(
      Math.max(dlha, cnc.minSirkaMm) + nadKratka,
      cnc.minSirkaMm,
    );
    // Pás má PEVNÚ šírku min. dĺžky — zmestiť doň viac kusov je zadarmo.
    let maxVPase = 1;
    while (
      kratka > 0 &&
      maxVPase < pocet &&
      sirkaZdruzenia(maxVPase + 1) <= cnc.minDlzkaMm
    ) {
      maxVPase += 1;
    }
    return rozdel(maxVPase, (n) =>
      prirez(
        dimDlhaOs,
        dimKratkaOs,
        1,
        n,
        n > 1 ? "rot_zdruz_rez" : "rot_rez",
      ),
    );
  }

  const hrubaDlha = nadMinimum(
    Math.max(dlha, cnc.minDlzkaMm) + nadDlha,
    cnc.minDlzkaMm,
  );
  const pas = (n: number): CncPrirez => {
    // Minimum stroja doťahuje LEN samostatný kus — združený pás si šírku
    // určuje sám (DAEX skript: 2× 36 mm dá 82, nie 100). Do KOLA 104 tu
    // zrkadlo clampovalo aj združený pás, takže tabuľka ukazovala iný
    // prírez, než rezal porez.
    const hrubaKratka =
      n > 1
        ? sirkaZdruzenia(n) + nadKratka
        : nadMinimum(
            Math.max(sirkaZdruzenia(n), cnc.minSirkaMm) + nadKratka,
            cnc.minSirkaMm,
          );
    let operacia: CncOperacia = "";
    if (n > 1) operacia = "zdruz_rez";
    else if (hrubaDlha > dlha + nadDlha || hrubaKratka > kratka + nadKratka)
      operacia = "rez";
    return prirez(hrubaDlha, hrubaKratka, 1, n, operacia);
  };

  // Združenie: prírez sa NAťahuje na min. šírku, tak doň dáme presne
  // toľko kusov, koľko ju vyplní — viac by blank len zväčšovalo.
  let vPrireze = 1;
  if (kratka < cnc.minSirkaMm && pocet >= 2 && kratka > 0) {
    while (vPrireze < pocet && sirkaZdruzenia(vPrireze) < cnc.minSirkaMm) {
      vPrireze += 1;
    }
  }
  return rozdel(vPrireze, pas);
}

/** Jeden kus uložený v príreze — offset od ľavého dolného rohu prírezu. */
export interface CncKusVPrireze {
  x: number;
  y: number;
  /** Rozmery kusu v osiach PRÍREZU — pri otočenom kuse sú prehodené. */
  sirkaX: number;
  sirkaY: number;
  /** Kus je v príreze otočený o 90° oproti vlastným osiam. */
  otoceny: boolean;
}

/** Tolerancia na porovnanie rozmerov (mm). */
const EPS_MM = 0.01;

/**
 * Kde v príreze kusy ležia. Prírez sa delí na **pásmo orezu** (nadmiera je
 * celková na rozmer, takže `nadmiera/2` na každej strane) a **využiteľnú
 * plochu** vnútri. Dielenské pravidlo: **kus nikdy nie je v strede** —
 * prilepí sa na hranu využiteľnej plochy, aby sa dal kolíkovať zo strany
 * (BohrHoriz do čela; pred takou hranou je len 2,5 mm odrezku):
 *
 * - pozdĺž osi, v ktorej sa kusy neradia: kus na začiatku plochy, vôľa na
 *   opačnom konci,
 * - pozdĺž osi radenia: krajné kusy na hranách plochy, zvyšná vôľa
 *   rovnomerne do `n−1` medzier (tie sú vždy ≥ rez píly).
 *
 * Rotovaný kus (kolíkovanie na čelách) je v príreze **otočený o 90°** —
 * dielec 300×80 sa do prírezu 205×305 inak ani nezmestí.
 */
export function rozlozKusy(
  prirez: CncPrirez,
  cistaX: number,
  cistaY: number,
  nadmieraXMm = 0,
  nadmieraYMm = 0,
): CncKusVPrireze[] {
  const n = prirez.kusovVPrireze;
  if (n < 1 || cistaX <= 0 || cistaY <= 0) return [];
  const { xJeDlha, dlha, kratka } = osiDielca(cistaX, cistaY);
  const otoceny =
    prirez.operacia === "rot_rez" || prirez.operacia === "rot_zdruz_rez";

  // Kusy sa radia vždy po svojej kratšej strane; líši sa len to, ktorá os
  // prírezu to je — pri rotácii je to os s min. dĺžkou stroja.
  const stackJeX = otoceny ? xJeDlha : !xJeDlha;
  // Pásmo orezu: nadmiera je celková na rozmer → polovica na každú stranu
  const padStack = (stackJeX ? nadmieraXMm : nadmieraYMm) / 2;
  const padFix = (stackJeX ? nadmieraYMm : nadmieraXMm) / 2;
  const stackPrirez = stackJeX ? prirez.x : prirez.y;
  const volno = stackPrirez - 2 * padStack - n * kratka;
  const krok = kratka + (n > 1 ? volno / (n - 1) : 0);

  return Array.from({ length: n }, (_, i) => {
    const pos = padStack + i * krok;
    return stackJeX
      ? { x: pos, y: padFix, sirkaX: kratka, sirkaY: dlha, otoceny }
      : { x: padFix, y: pos, sirkaX: dlha, sirkaY: kratka, otoceny };
  });
}

/**
 * Ktoré hrany kusu sú prístupné pre kolíkovanie zo strany — tie, pred
 * ktorými nie je nič okrem **pásma orezu** (`nadmiera/2`). Hrana hľadiaca
 * do medzery s iným kusom prístupná nie je.
 *
 * Kus v rohu má dve prístupné hrany, kus obklopený len orezom až štyri;
 * stredný kus pri troch a viac združených kusoch nemá voľnú dlhú hranu.
 * Pri nadmiere 0 to degeneruje na „presne na okraji prírezu".
 */
export function hranyPristupne(
  kus: CncKusVPrireze,
  prirez: CncPrirez,
  nadmieraXMm = 0,
  nadmieraYMm = 0,
): { vlavo: boolean; vpravo: boolean; dole: boolean; hore: boolean } {
  const okrajX = nadmieraXMm / 2 + EPS_MM;
  const okrajY = nadmieraYMm / 2 + EPS_MM;
  return {
    vlavo: kus.x <= okrajX,
    vpravo: prirez.x - (kus.x + kus.sirkaX) <= okrajX,
    dole: kus.y <= okrajY,
    hore: prirez.y - (kus.y + kus.sirkaY) <= okrajY,
  };
}

/** Rozmer do označenia — bez zbytočných desatinných núl. */
function mm(hodnota: number): string {
  return String(Math.round(hodnota * 10) / 10);
}

/** Používateľský názov operácie (z Nastavení, inak default). */
export function cncNazovOperacie(
  operacia: CncOperacia,
  cnc: CncUpnutie | null,
): string {
  if (operacia === "") return "";
  return (
    (cnc?.nazvy?.[operacia] ?? "").trim() || CNC_NAZVY_DEFAULT[operacia]
  );
}

/**
 * Rozmer pre človeka — `302`, `51,5` (stotiny, desatinná čiarka, bez
 * medzery tisícok ako stĺpce rozmerov). Zrkadlo `prirez_tabula.mm_text`.
 */
export function mmText(hodnota: number): string {
  return String(Math.round(hodnota * 100) / 100).replace(".", ",");
}

/**
 * Označenie prírezu pre dielňu — formát VBA (pole ZPRACOVANI):
 * `nc_zdruz_rez_2x150x40`, `nc_rez150x40`. Bez operácie prázdny reťazec.
 *
 * `cistaX/Y` je rozmer **v okamihu rezu** (`rez_x_mm` zo servera, KOLO 195)
 * ako v backende `cnc_oznacenie` — dielec 302×52 s hranami sa reže
 * 302×51,5; bez neho čistý rozmer.
 */
export function cncOznacenie(
  prirez: CncPrirez,
  cistaX: number,
  cistaY: number,
  cnc: CncUpnutie | null,
): string {
  if (prirez.operacia === "") return "";
  const nazov = cncNazovOperacie(prirez.operacia, cnc);
  const dlha = Math.max(cistaX, cistaY);
  const kratka = Math.min(cistaX, cistaY);
  const nasobok = prirez.kusovVPrireze > 1 ? `_${prirez.kusovVPrireze}x` : "";
  return `${nazov}${nasobok}${mm(dlha)}x${mm(kratka)}`;
}

/** Prečo dielec dostal iný hrubý rozmer než čistá+nadmiera. */
export type CncDovod =
  | "ziadny" // nie CNC dielec, alebo bez rozmerov
  | "nadmiera" // len bežná nadmiera (žiadny clamp/rotácia)
  | "predrez" // malý dielec zväčšený na minimum upnutia
  | "zdruzenie" // úzke kusy v jednom príreze
  | "rotacia"; // otočený o 90° kvôli čelám

export interface CncStav {
  /** Ide dielec na CNC stroj (platí naň minimum upnutia)? */
  cncDielec: boolean;
  /** Hrubý rozmer prírezu 1 ks (os X × os Y dielca). */
  hrubyX: number;
  hrubyY: number;
  dovod: CncDovod;
  /** Prírezy pre celý počet ks (kvôli združeniu). */
  prirezy: CncPrirez[];
  /** Aplikuje sa clamp/rotácia/združenie (hrubý > čistá+nadmiera)? */
  zvacsene: boolean;
  /** Operácia hlavného (prvého) prírezu. */
  operacia: CncOperacia;
  /** Názov operácie z Nastavení (prázdny = bez operácie). */
  nazovOperacie: string;
  /** Popis operácie po slovensky (prázdny = bez operácie). */
  popisOperacie: string;
  /** Označenie hlavného prírezu pre dielňu (prázdne = bez operácie). */
  oznacenie: string;
  /** Koľko prírezov celkovo pôjde na stroj (kusy sa NEmenia). */
  pocetPrirezov: number;
  /** Kusov dielca v hlavnom príreze (>1 = združený). */
  kusovVPrireze: number;
  /**
   * Na čo sa zväčšený prírez po stroji dorezáva (KOLO 195) — dlhšia
   * strana prvá, ako v označení. Bez rozmeru zo servera čistý rozmer.
   */
  rez: { dlha: number; kratka: number };
  /** Uloženie kusov v hlavnom príreze (na kreslenie náhľadu). */
  kusy: CncKusVPrireze[];
  /**
   * Efektívna nadmiera (0 keď je vypnutá) — celková na rozmer, teda
   * `nadmiera/2` je pásmo orezu na každej strane prírezu.
   */
  nadmiera: { x: number; y: number };
}

/**
 * Jeden zdroj pravdy pre zobrazenie CNC prírezu — panel aj tabuľka.
 * Berie čisté rozmery + nadmieru + operáciu + rotačný flag a vráti stav
 * (dôvod + hrubé rozmery + označenie). Zrkadlí backend
 * `mnozstvo_mj`/`cnc_prirezy`.
 */
export function cncStav(args: {
  xMm: number;
  yMm: number;
  nadmieraXMm: number;
  nadmieraYMm: number;
  nadmieraAktivna: boolean;
  pocet: number;
  /** `Operation.je_cnc` vybraného postupu (KOLO 108) — nie jeho kód. */
  operaciaJeCnc: boolean | null | undefined;
  nazovStrojovy: string | null | undefined;
  cncRotovat: boolean | null | undefined;
  /** `null` = odvoď z postupu; inak explicitné rozhodnutie (KOLO 105). */
  ideNaCnc?: boolean | null;
  cnc: CncUpnutie | null;
  /**
   * Rozmer v okamihu rezu zo servera (`rez_x_mm`/`rez_y_mm`, KOLO 195) —
   * predfrez postupu a hrany pozná len backend. Chýba = čistý rozmer.
   */
  rezXMm?: number | null;
  rezYMm?: number | null;
}): CncStav {
  const {
    xMm, yMm, nadmieraXMm, nadmieraYMm, nadmieraAktivna, pocet,
    operaciaJeCnc, nazovStrojovy, cncRotovat, ideNaCnc, cnc,
  } = args;
  const rezX = args.rezXMm ?? xMm;
  const rezY = args.rezYMm ?? yMm;
  const nadX = nadmieraAktivna ? nadmieraXMm : 0;
  const nadY = nadmieraAktivna ? nadmieraYMm : 0;
  const cncDielec = ideNaNc(operaciaJeCnc, ideNaCnc);
  const cncEff = cncDielec ? cnc : null;
  const rotovat = dielecRotovat(nazovStrojovy, cncRotovat);
  const kusov = Math.max(1, pocet);
  const prirezy = cncPrirezy(xMm, yMm, nadX, nadY, kusov, cncEff, rotovat);
  const hlavny = prirezy[0] ?? {
    x: xMm + nadX,
    y: yMm + nadY,
    pocet: 1,
    kusovVPrireze: 1,
    operacia: "" as const,
  };
  const rotovane =
    rotovat &&
    cncEff != null &&
    cncEff.rotaciaMaxMm > 0 &&
    Math.max(xMm, yMm) <= cncEff.rotaciaMaxMm;
  const zdruzene = prirezy.some((p) => p.kusovVPrireze > 1);
  // hrubý rozmer 1 ks: pri združení je prírez spoločný, tak ukazujeme
  // rozmer prírezu tak, ako pôjde na pílu
  const zvacsene = hlavny.x > xMm + nadX || hlavny.y > yMm + nadY;

  let dovod: CncDovod = "ziadny";
  if (cncDielec && xMm > 0 && yMm > 0) {
    if (rotovane) dovod = "rotacia";
    else if (zdruzene) dovod = "zdruzenie";
    else if (zvacsene) dovod = "predrez";
    else dovod = "nadmiera";
  }
  return {
    cncDielec,
    hrubyX: hlavny.x,
    hrubyY: hlavny.y,
    dovod,
    prirezy,
    zvacsene: zvacsene || rotovane || zdruzene,
    operacia: hlavny.operacia,
    nazovOperacie: cncNazovOperacie(hlavny.operacia, cncEff),
    popisOperacie:
      hlavny.operacia === "" ? "" : CNC_OPERACIE_POPIS[hlavny.operacia],
    oznacenie: cncOznacenie(hlavny, rezX, rezY, cncEff),
    pocetPrirezov: prirezy.reduce((s, p) => s + p.pocet, 0),
    kusovVPrireze: hlavny.kusovVPrireze,
    rez: { dlha: Math.max(rezX, rezY), kratka: Math.min(rezX, rezY) },
    kusy: rozlozKusy(hlavny, xMm, yMm, nadX, nadY),
    nadmiera: { x: nadX, y: nadY },
  };
}
